'use client';

import { cn } from '@insta-club/utilities';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select, {
    ClassNamesConfig,
    GroupBase,
    MultiValue,
    OnChangeValue,
    Props as RSProps,
    SingleValue,
    StylesConfig
} from 'react-select';

export type OptionBase = { value: string | number; label: string };

type FormValue<Opt extends OptionBase, M extends boolean, R extends boolean> = R extends true
    ? M extends true
        ? Opt[]
        : Opt | null
    : M extends true
      ? Array<Opt['value']>
      : Opt['value'] | null;

const containerVariants = cva(
    'w-full relative rounded-lg border px-3 focus-within:border-primary-500 bg-transparent text-sm transition-all duration-200',
    {
        variants: {
            variant: {
                default: 'border-gray-300 focus-within:border-primary-500',
                error: 'border-primary-500 focus-within:border-primary-500'
            },
            size: {
                default: 'h-14',
                sm: 'h-12',
                lg: 'h-16'
            }
        },
        defaultVariants: { variant: 'default', size: 'default' }
    }
);

const floatingLabelVariants = cva(
    'absolute right-3 text-gray-500 transition-all duration-200 pointer-events-none bg-white px-1',
    {
        variants: {
            variant: {
                default: 'peer-focus:text-primary-500',
                error: 'text-primary-500 peer-focus:text-primary-500'
            },
            size: {
                default: 'top-4',
                sm: 'top-3',
                lg: 'top-5'
            }
        },
        defaultVariants: { variant: 'default', size: 'default' }
    }
);

export interface FloatingSelectProps<
    Opt extends OptionBase = OptionBase,
    M extends boolean = false,
    R extends boolean = false,
    G extends GroupBase<Opt> = GroupBase<Opt>
> extends Omit<RSProps<Opt, M, G>, 'name' | 'options' | 'onChange' | 'value' | 'classNamePrefix'>,
        VariantProps<typeof containerVariants> {
    name: string;
    label?: string;
    options: ReadonlyArray<Opt>;
    containerClassName?: string;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    fullWidth?: boolean;
    isRtl?: boolean;
    returnOption?: R;
    menuPortalTarget?: HTMLElement | null | undefined;
}

function makeStyles<Opt extends OptionBase, M extends boolean, G extends GroupBase<Opt>>(
    size: 'sm' | 'default' | 'lg' | undefined,
    isDisabled: boolean | undefined,
    isRtl: boolean
): StylesConfig<Opt, M, G> {
    const minHeight = size === 'sm' ? 48 : size === 'lg' ? 64 : 56;
    return {
        control: (base) => ({
            ...base,
            backgroundColor: 'transparent',
            border: 0,
            boxShadow: 'none',
            minHeight,
            cursor: isDisabled ? 'not-allowed' : 'default',
            paddingTop: 8,
            paddingBottom: 6,
            direction: isRtl ? 'rtl' : 'ltr'
        }),
        valueContainer: (b) => ({ ...b, paddingInline: 0 }),
        indicatorsContainer: (b) => ({ ...b, gap: 4 }),
        placeholder: (b) => ({ ...b, opacity: 0 }),
        singleValue: (b) => ({ ...b, marginInlineStart: 0 }),
        input: (b) => ({ ...b, margin: 0, padding: 0 }),
        menuPortal: (b) => ({ ...b, zIndex: 9999 }),
        menu: (b) => ({
            ...b,
            zIndex: 9999,
            direction: isRtl ? 'rtl' : 'ltr',
            textAlign: isRtl ? 'right' : 'left',
            right: isRtl ? 0 : undefined,
            left: isRtl ? 'auto' : undefined
        })
    };
}

export default function FloatingSelect<
    Opt extends OptionBase = OptionBase,
    M extends boolean = false,
    R extends boolean = false,
    G extends GroupBase<Opt> = GroupBase<Opt>
>({
    name,
    label = 'لیبل پیشفرض',
    options,
    returnOption = false as R,
    startAdornment,
    endAdornment,
    isDisabled,
    isClearable = true,
    fullWidth,
    containerClassName,
    variant,
    size,
    isRtl = true,
    menuPortalTarget,
    isMulti,
    classNames,
    ...rest
}: FloatingSelectProps<Opt, M, R, G>) {
    const {
        control,
        formState: { errors }
    } = useFormContext();

    const sizeSafe: 'default' | 'sm' | 'lg' | undefined = (size ?? undefined) as 'default' | 'sm' | 'lg' | undefined;
    const inputVariant: 'default' | 'error' | undefined = (
        errors as Record<string, { message?: string } | undefined>
    )?.[name]
        ? 'error'
        : (variant ?? undefined);

    const [focused, setFocused] = React.useState<boolean>(false);
    const styles = makeStyles<Opt, M, G>(sizeSafe, isDisabled, isRtl);

    type RSValue = SingleValue<Opt> | MultiValue<Opt>;
    const isMultiBool: boolean = Boolean(isMulti);

    const findOption = (v: Opt['value']): Opt | undefined => options.find((o) => o.value === v);

    const toSelectValue = (value: FormValue<Opt, M, R>): RSValue => {
        if (isMultiBool) {
            if (returnOption) {
                const arr = Array.isArray(value) ? (value as Opt[]) : [];
                return arr;
            }
            const ids = Array.isArray(value) ? (value as Array<Opt['value']>) : [];
            const mapped = ids.map((id) => findOption(id)).filter((o): o is Opt => o !== undefined);
            return mapped;
        }
        if (returnOption) {
            return (value as Opt | null) ?? null;
        }
        const id = value as Opt['value'] | null;
        return id == null ? null : (findOption(id) ?? null);
    };

    const fromSelectValue = (selected: OnChangeValue<Opt, M>): FormValue<Opt, M, R> => {
        if (isMultiBool) {
            const arr = Array.isArray(selected) ? (selected as Opt[]) : [];
            return (returnOption ? arr : arr.map((o) => o.value)) as FormValue<Opt, M, R>;
        }
        const single = (Array.isArray(selected) ? null : (selected as SingleValue<Opt>)) ?? null;
        return (returnOption ? single : (single?.value ?? null)) as FormValue<Opt, M, R>;
    };

    const mergedClassNames: ClassNamesConfig<Opt, M, G> = {
        control: (state) => cn('min-h-0', classNames?.control?.(state)),
        menu: (state) => cn('rounded-md shadow-lg', classNames?.menu?.(state)),
        option: (state) =>
            cn(
                'cursor-pointer',
                state.isSelected && '!bg-primary-500 !text-white',
                !state.isSelected && state.isFocused && '!bg-primary-100 !text-primary-900',
                classNames?.option?.(state)
            ),
        multiValue: (state) => cn('!bg-primary-100 rounded-lg px-1', classNames?.multiValue?.(state)),
        multiValueLabel: (state) => cn('!text-primary-900 font-medium', classNames?.multiValueLabel?.(state)),
        multiValueRemove: (state) =>
            cn('!text-primary-900 hover:!bg-primary-500 hover:!text-white', classNames?.multiValueRemove?.(state))
    };

    return (
        <div className={cn('flex flex-col gap-1', fullWidth && 'w-full')}>
            <div
                className={cn(
                    containerVariants({ variant: inputVariant, size: sizeSafe }),
                    isDisabled && 'cursor-not-allowed bg-gray-50 opacity-50',
                    containerClassName
                )}
            >
                <div className='flex h-full w-full items-center gap-2'>
                    {startAdornment && <div className='flex items-center gap-1'>{startAdornment}</div>}

                    <Controller
                        name={name}
                        control={control}
                        render={({ field }) => {
                            const currentRSValue: RSValue = toSelectValue(field.value as FormValue<Opt, M, R>);
                            const hasValue = Array.isArray(field.value)
                                ? field.value.length > 0
                                : field.value != null && String(field.value).length > 0;
                            const shouldFloat = focused || hasValue;

                            return (
                                <div className='relative flex-1'>
                                    <Select<Opt, M, G>
                                        classNamePrefix='ic-select'
                                        options={options as Opt[]}
                                        isDisabled={isDisabled}
                                        isClearable={isClearable}
                                        isMulti={isMulti}
                                        onFocus={() => setFocused(true)}
                                        onBlur={() => setFocused(false)}
                                        styles={styles}
                                        classNames={mergedClassNames}
                                        menuPlacement='auto'
                                        menuPosition='fixed'
                                        menuPortalTarget={
                                            menuPortalTarget ??
                                            (typeof window !== 'undefined' ? document.body : undefined)
                                        }
                                        placeholder=' '
                                        value={currentRSValue as OnChangeValue<Opt, M>}
                                        onChange={(val) => field.onChange(fromSelectValue(val))}
                                        {...(rest as RSProps<Opt, M, G>)}
                                    />

                                    <label
                                        className={cn(
                                            floatingLabelVariants({ variant: inputVariant, size: sizeSafe }),
                                            shouldFloat && '-top-2 text-xs',
                                            isDisabled && 'bg-gray-50'
                                        )}
                                    >
                                        {label}
                                    </label>
                                </div>
                            );
                        }}
                    />

                    {endAdornment && <div className='flex items-center gap-1'>{endAdornment}</div>}
                </div>
            </div>

            {(errors as Record<string, { message?: string } | undefined>)?.[name]?.message && (
                <p className='text-right text-sm text-red-500'>
                    {(errors as Record<string, { message?: string } | undefined>)[name]?.message as string}
                </p>
            )}
        </div>
    );
}

